import { useEffect, useMemo, useRef } from 'react';

import { useSession } from '@eneco/auth';
import { useSelfServiceAccount } from '@eneco/auth-be-self-service';
import env from '@eneco/common/env';
import RichText from '@eneco/components/RichText/RichText';
import {
  ConsumptionTypeDto,
  ConsumptionResponse,
  PeakResponse,
  getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierServiceDeliveryPoints,
} from '@eneco/dc-be/client';
import { PeakAverageResponse, TimeFrameDto } from '@eneco/dc-be/client/types.gen';
import useAuthenticatedDCBE from '@eneco/dc-be/hooks/useAuthenticatedDCBE';
import unwrapData from '@eneco/dc-be/utils/unwrapData';
import { InferGetServerSidePropsType, useRouter } from '@eneco/next';
import {
  Bleed,
  Box,
  Card,
  Divider,
  Grid,
  Heading,
  IconButton,
  InputSelect,
  NotificationBox,
  Stack,
  Stretch,
  Text,
} from '@eneco/sparky';
import { useMediaQuery } from '@eneco/sparky/hooks';
import { ChevronLeftIcon, ChevronRightIcon } from '@eneco/sparky/icons';
import { styled } from '@eneco/sparky/stitches';
import { useTracking } from '@eneco/tracking';
import { useContent } from '@sitecore/common';
import { ConsumptionGraphicalOverviewRendering } from '@sitecore/types/ConsumptionGraphicalOverview';
import { ParentSize } from '@visx/responsive';
import { StringParam, useQueryParam } from 'use-query-params';

import CapacityRateInformationBox from './components/CapacityRateInformationBox';
import { ConsumptionGraphicalOverviewGraph } from './components/ConsumptionGraphicalOverviewGraph';
import MeterReadingsTable from './components/MeterReadingTable';
import PeakValuesConsumptionData from './components/PeakValuesConsumptionData';
import PeakValuesConsumptionDataLegend from './components/PeakValuesConsumptionDataLegend';
import StandardConsumptionData from './components/StandardConsumptionData';
import StandardConsumptionDataLegend from './components/StandardConsumptionDataLegend';
import TotalAdvancesCardWrapper from './components/TotalAdvancesCardWrapper';
import { ConsumptionGraphicalOverviewSkeleton } from './ConsumptionGraphicalOverviewSkeleton';
import { useConsumptionGraphTimeFrame } from './hooks/useConsumptionGraphTimeFrame';
import { getSelectedPeriod, useSelectedPeriod } from './hooks/useSelectedPeriod';
import { AddressFilter } from '../../../components/AddressFilter';
import DataErrorNotification from '../../../components/DataErrorNotification';
import InfoComponent from '../../../components/InfoComponent';
import { useAddressOptions } from '../../../hooks/useAddressOptions';
import { useBillingAccount } from '../../../hooks/useBillingAccount';
import { IsAddressInWalloniaOrBrussels } from '../../../utils/addressUtils';

const ScrollableContainer = styled('div', {
  maxWidth: '100%',
  overflowX: 'scroll',
});

const GRANULARITIES_LOWER_THAN_MONTHLY = ['Daily', 'Hourly', 'FifteenMin', 'Quarterly'];

export const getServerSideProps = () => {
  const showPeakValues =
    env('ENERGY_MONITORING_SHOW_PEAKVALUES') === '1' || env('FE_ENERGY_MONITORING_SHOW_PEAKVALUES') === '1';

  return { props: { showPeakValues } };
};

const ConsumptionGraphicalOverview = ({ showPeakValues }: InferGetServerSidePropsType<typeof getServerSideProps>) => {
  const { fields } = useContent<ConsumptionGraphicalOverviewRendering>();
  const { push } = useRouter();
  const { trackEventBE } = useTracking();
  const {
    isProspect,
    selectedAccount: { crmAccountNumber: accountNumber },
  } = useSelfServiceAccount();
  const { data: session, isAuthPending } = useSession();
  const { addressIdentifier, currentAddress, isLoading: isLoadingAddresses } = useAddressOptions();
  const { hasBillingAccount, hasActiveContract, isBillingContextLoading } = useBillingAccount({
    addressIdentifier,
  });

  const periodOptions = fields.data.periodOptionsList.value.enum;

  const consumptionTypeOptions = showPeakValues
    ? fields.data.consumptionTypeOptionsList.value.enum
    : fields.data.consumptionTypeOptionsList.value.enum.filter(option => option.name !== 'PeakValues');

  const [consumptionType, setConsumptionType] = useQueryParam('consumptionType', StringParam, {
    updateType: 'replaceIn',
  });
  const {
    timeFrame,
    setTimeFrame,
    startDate,
    endDate,
    getPeriodTitle,
    changePeriod,
    isDisabledNextPeriod,
    consumptionFetchSignal,
  } = useConsumptionGraphTimeFrame();
  const { setSelected } = useSelectedPeriod();
  const isDesktop = useMediaQuery('lg');

  const {
    data: serviceDeliveryPoints,
    isLoading: isLoadingServiceDeliveryPoints,
    error: errorServiceDeliveryPoints,
  } = useAuthenticatedDCBE(
    getEnecoBeXapiSiteApiV1AccountsByAccountNumberDeliveryAddressesByAddressIdentifierServiceDeliveryPoints,
    {
      path: {
        accountNumber,
        addressIdentifier,
      },
      query: {
        includeMetersFromTerminatedAccounts: true,
      },
    },
    [`/accounts/${accountNumber}/delivery-addresses/${addressIdentifier}/service-delivery-points`],
    session,
  );

  const hasTracked = useRef(false);
  useEffect(() => {
    if (hasTracked.current) return;
    hasTracked.current = true;
    trackEventBE('myeneco_consumption_graphical_overview', {
      status: 'pageView',
      data: { userIsLoggedIn: !!session },
    });
  }, [session, trackEventBE]);

  const isAddressInWalloniaOrBrussels = IsAddressInWalloniaOrBrussels(currentAddress?.details);
  const serviceDeliveryPointsData = unwrapData(serviceDeliveryPoints);
  const hasDigitalElecMeter = serviceDeliveryPointsData?.serviceDeliveryPoints?.some(
    serviceDeliveryPoint => serviceDeliveryPoint.meterType === 'Digital' && serviceDeliveryPoint.type === 'Electricity',
  );
  const isAnalogueMeter = serviceDeliveryPointsData?.serviceDeliveryPoints?.some(
    serviceDeliveryPoint =>
      serviceDeliveryPoint.meterType === 'Analogue' && serviceDeliveryPoint.type === consumptionType,
  );
  const hasSmr3MeterRegime = serviceDeliveryPointsData?.serviceDeliveryPoints?.some(
    serviceDeliveryPoint => serviceDeliveryPoint.meterRegime?.current === 'Smr3',
  );
  const hasElectricityMeter = serviceDeliveryPointsData?.serviceDeliveryPoints?.some(
    serviceDeliveryPoint => serviceDeliveryPoint.type === 'Electricity',
  );
  const hasGasMeter = serviceDeliveryPointsData?.serviceDeliveryPoints?.some(
    serviceDeliveryPoint => serviceDeliveryPoint.type === 'Gas',
  );

  const electricityServiceDeliveryPoint = serviceDeliveryPointsData?.serviceDeliveryPoints?.find(
    serviceDeliveryPoint => serviceDeliveryPoint.type === 'Electricity',
  );

  const showSmr3GranularityFeature = fields.data.showSmr3GranularityFeatureFlag?.value ?? false;
  const hasSolarPanels = Boolean(
    electricityServiceDeliveryPoint?.serviceComponent &&
      (electricityServiceDeliveryPoint.serviceComponent.includes('Injection') ||
        electricityServiceDeliveryPoint.serviceComponent === 'Compensation' ||
        electricityServiceDeliveryPoint.serviceComponent === 'CompensatedOfftake'),
  );
  const hasSmr1MeterRegime = electricityServiceDeliveryPoint?.meterRegime?.current === 'Smr1';
  const smr3ChangeRequestPending = electricityServiceDeliveryPoint?.meterRegime?.changeRequestPending ?? false;

  const hideHighGranularityForSolarSmr1 = !showSmr3GranularityFeature && hasSolarPanels && hasSmr1MeterRegime;
  const showSmr3SwitchCta =
    !hasSmr3MeterRegime &&
    (showSmr3GranularityFeature || (!showSmr3GranularityFeature && !hasSolarPanels)) &&
    (timeFrame === 'Daily' ||
      timeFrame === 'Hourly' ||
      timeFrame === 'FifteenMin' ||
      consumptionType === 'PeakValues') &&
    consumptionType !== 'Gas';

  const filteredPeriodOptions = useMemo(() => {
    let options: typeof periodOptions;
    switch (true) {
      case isAnalogueMeter:
        options = periodOptions.filter(
          periodOption => periodOption.value === 'Monthly' || periodOption.value === 'Yearly',
        );
        break;
      case consumptionType === 'PeakValues':
      case consumptionType === 'Total':
      case consumptionType === 'Gas':
        options = periodOptions.filter(
          periodOption => periodOption.value === 'Monthly' || periodOption.value === 'Yearly',
        );
        break;
      case consumptionType === 'Electricity':
      case consumptionType === 'Injection':
        options = periodOptions;
        break;
      default:
        options = periodOptions.filter(
          periodOption => periodOption.value === 'Monthly' || periodOption.value === 'Yearly',
        );
    }
    if (hideHighGranularityForSolarSmr1) {
      options = options.filter(periodOption => !GRANULARITIES_LOWER_THAN_MONTHLY.includes(periodOption.value));
    }
    return options;
  }, [consumptionType, periodOptions, isAnalogueMeter, hideHighGranularityForSolarSmr1]);

  const filteredConsumptionTypeOptions = useMemo(() => {
    let options;
    switch (true) {
      case hasElectricityMeter && hasGasMeter:
        options = consumptionTypeOptions;
        break;
      case hasElectricityMeter:
        options = consumptionTypeOptions.filter(option => option.value !== 'Gas');
        break;
      case hasGasMeter:
        options = consumptionTypeOptions.filter(
          option => option.value !== 'Electricity' && option.value !== 'PeakValues' && option.value !== 'Injection',
        );
        break;
      default:
        options = consumptionTypeOptions;
    }

    if (!hasSolarPanels) {
      return options.filter(option => option.value !== 'Injection');
    }

    if (isAddressInWalloniaOrBrussels) {
      return options.filter(option => option.value !== 'PeakValues');
    }

    return options;
  }, [consumptionTypeOptions, hasElectricityMeter, hasGasMeter, isAddressInWalloniaOrBrussels, hasSolarPanels]);

  useEffect(() => {
    if (!consumptionType) {
      setConsumptionType('Total');
    }
    setSelected(getSelectedPeriod());
    const periodOptionExists = filteredPeriodOptions.some(periodOption => periodOption.value === timeFrame);
    if (!periodOptionExists) {
      setTimeFrame('Monthly');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consumptionType]);

  useEffect(() => {
    if (!timeFrame) {
      setTimeFrame('Monthly');
    }
    setSelected(getSelectedPeriod());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFrame]);

  useEffect(() => {
    const consumptionTypeOptionExists = filteredConsumptionTypeOptions.some(
      consumptionTypeOption => consumptionTypeOption.value === consumptionType,
    );
    if (!consumptionTypeOptionExists) {
      setConsumptionType('Total');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredConsumptionTypeOptions]);

  useEffect(() => {
    if (isProspect) {
      push(fields.data.redirectLink.value.href);
    }
  }, [fields.data.redirectLink.value.href, isProspect, push]);

  const isPageLoading =
    isAuthPending || isLoadingAddresses || isLoadingServiceDeliveryPoints || isBillingContextLoading;

  if (isProspect) {
    return null;
  }

  if (isPageLoading) {
    return <ConsumptionGraphicalOverviewSkeleton />;
  }

  if (errorServiceDeliveryPoints) {
    return <DataErrorNotification />;
  }

  const periodTitle = getPeriodTitle(timeFrame ?? 'Monthly');

  const showAnalogueMeterMessage = !hasDigitalElecMeter && consumptionType === 'PeakValues';

  const renderConsumptionContent = (
    consumptionData: ConsumptionResponse | PeakResponse | null,
    peakValueAverage: PeakAverageResponse | null,
    isLoading: boolean,
  ) => {
    const isHighFrequencyMobile =
      (timeFrame === 'FifteenMin' || timeFrame === 'Hourly' || timeFrame === 'Daily') && !isDesktop;

    const graphProps = {
      isLoading: isLoading || isLoadingServiceDeliveryPoints,
      timeFrame: (timeFrame ?? 'Monthly') as TimeFrameDto,
      consumptionType: (consumptionType as ConsumptionTypeDto) || 'Total',
      consumptionData,
      height: 500,
      showAnalogueMeterMessage,
      peakValueAverage,
      showSmr3SwitchCta,
      smr3ChangeRequestPending,
      contractId: electricityServiceDeliveryPoint?.contractId,
    };

    return (
      <Bleed top="4">
        {isHighFrequencyMobile ? (
          <ScrollableContainer>
            <ConsumptionGraphicalOverviewGraph {...graphProps} width={800} />
          </ScrollableContainer>
        ) : (
          <ParentSize>{({ width }) => <ConsumptionGraphicalOverviewGraph {...graphProps} width={width} />}</ParentSize>
        )}
      </Bleed>
    );
  };

  return (
    <Stretch>
      <Grid
        gridTemplateColumns="repeat(19, 1fr)"
        gridTemplateRows={{ initial: 'auto 1fr', lg: '1fr' }}
        rowGap={6}
        columnGap={{ md: 6 }}>
        <Grid.Item gridColumn={'-1/1'}>
          <Box paddingTop="10">
            <Heading as="h1" size={{ initial: 'M', lg: 'L' }}>
              {fields.data.title.value}
            </Heading>
          </Box>
        </Grid.Item>
        <Grid.Item gridColumn={{ initial: '-1/1', lg: '1/14' }}>
          <Stack gap="6">
            <Stack direction="row" alignX="justify" wrap gap={4}>
              <Stack.Item>
                <AddressFilter label={fields.data.addressFilterLabel.value} />
              </Stack.Item>
              <Stack gap="2" direction="row" wrap>
                <InputSelect
                  label={fields.data.consumptionTypeLabel.value}
                  name="consumptionType"
                  options={filteredConsumptionTypeOptions}
                  placeholder=""
                  value={(consumptionType as ConsumptionTypeDto) || 'Total'}
                  onChange={e => setConsumptionType(e.currentTarget.value as ConsumptionTypeDto)}
                />
                <InputSelect
                  label={fields.data.periodLabel.value}
                  name="period"
                  options={filteredPeriodOptions}
                  placeholder=""
                  value={timeFrame ?? 'Monthly'}
                  onChange={e => setTimeFrame(e.currentTarget.value as TimeFrameDto)}
                />
              </Stack>
            </Stack>
            {!hasBillingAccount && (
              <NotificationBox
                isAlert={false}
                title={fields.data.noBillingAccountNotification?.value?.title}
                text={<RichText html={fields.data.noBillingAccountNotification?.value?.content} />}
                variant={fields.data.noBillingAccountNotification?.value?.variant}
              />
            )}
            {hasBillingAccount && !hasActiveContract && (
              <NotificationBox
                isAlert={false}
                title={fields.data.noActiveContractNotification?.value?.title}
                text={<RichText html={fields.data.noActiveContractNotification?.value?.content} />}
                variant={fields.data.noActiveContractNotification?.value?.variant}
              />
            )}
            <Card>
              <Stack>
                <Box padding="4">
                  <Stack alignX="justify" alignY="center" direction="row" gap="2">
                    <IconButton label="Previous period" onClick={() => changePeriod('previous')}>
                      <ChevronLeftIcon />
                    </IconButton>
                    <Heading as="h2" size="2XS">
                      {/* eslint-disable-next-line dxp-rules/no-inline-css */}
                      <span style={{ textTransform: 'capitalize' }}>{periodTitle}</span>
                    </Heading>
                    <IconButton
                      isDisabled={isDisabledNextPeriod()}
                      label="Next period"
                      onClick={() => changePeriod('next')}>
                      <ChevronRightIcon />
                    </IconButton>
                  </Stack>
                </Box>
                <Divider />
                <Box paddingX="6" paddingTop="5">
                  <Stack direction="row" alignX="justify">
                    <Heading as="h3" size="3XS">
                      {fields.data.graphTitle.value}
                    </Heading>
                  </Stack>
                </Box>
                <Box paddingRight={{ initial: '1', md: '6' }} paddingBottom="8">
                  {consumptionType === 'PeakValues' ? (
                    <PeakValuesConsumptionData
                      timeFrame={(timeFrame ?? 'Monthly') as TimeFrameDto}
                      startDate={startDate}
                      endDate={endDate}
                      signal={consumptionFetchSignal}>
                      {({ consumptionData, peakValueAverage, isLoading }) =>
                        renderConsumptionContent(consumptionData, peakValueAverage, isLoading)
                      }
                    </PeakValuesConsumptionData>
                  ) : (
                    <StandardConsumptionData
                      consumptionType={consumptionType as Exclude<ConsumptionTypeDto, 'PeakValues'>}
                      timeFrame={(timeFrame ?? 'Monthly') as TimeFrameDto}
                      startDate={startDate}
                      endDate={endDate}
                      showSmr3SwitchCta={showSmr3SwitchCta}
                      isGraph
                      signal={consumptionFetchSignal}>
                      {({ consumptionData, isLoading }) => renderConsumptionContent(consumptionData, null, isLoading)}
                    </StandardConsumptionData>
                  )}
                </Box>
              </Stack>
            </Card>
            {consumptionType === 'PeakValues' && !showAnalogueMeterMessage && (
              <Stack direction="row" alignY="center" gap="1">
                <Text>{fields.data.disclaimerText.value}</Text>
                <InfoComponent variant="icon" triggerText={fields.data.disclaimerText.value}>
                  <RichText html={fields.data.disclaimerInfoDescription.value} />
                </InfoComponent>
              </Stack>
            )}
            {consumptionType !== 'Total' && consumptionType !== 'PeakValues' && isDesktop && (
              <MeterReadingsTable
                consumptionType={(consumptionType as ConsumptionTypeDto) || 'Total'}
                fields={fields.meterReadingsTable}
                serviceDeliveryData={serviceDeliveryPointsData}
              />
            )}
          </Stack>
        </Grid.Item>
        <Grid.Item gridColumn={{ initial: '-1/1', lg: '14/19' }}>
          <Stack gap="4">
            {consumptionType === 'PeakValues' ? (
              <PeakValuesConsumptionData
                timeFrame={(timeFrame ?? 'Monthly') as TimeFrameDto}
                startDate={startDate}
                endDate={endDate}
                signal={consumptionFetchSignal}>
                {({ consumptionData, peakValueAverage }) => (
                  <PeakValuesConsumptionDataLegend
                    consumptionData={consumptionData}
                    timeFrame={(timeFrame ?? 'Monthly') as TimeFrameDto}
                    showAnalogueMeterMessage={showAnalogueMeterMessage}
                    consumptionType={consumptionType as ConsumptionTypeDto}
                    peakValueAverage={peakValueAverage}
                  />
                )}
              </PeakValuesConsumptionData>
            ) : (
              <StandardConsumptionData
                consumptionType={consumptionType as Exclude<ConsumptionTypeDto, 'PeakValues'>}
                timeFrame={(timeFrame ?? 'Monthly') as TimeFrameDto}
                startDate={startDate}
                endDate={endDate}
                showSmr3SwitchCta={showSmr3SwitchCta}
                signal={consumptionFetchSignal}>
                {({ consumptionData }) => (
                  <StandardConsumptionDataLegend
                    consumptionData={consumptionData}
                    consumptionType={consumptionType as Exclude<ConsumptionTypeDto, 'PeakValues'>}
                    timeFrame={(timeFrame ?? 'Monthly') as TimeFrameDto}
                  />
                )}
              </StandardConsumptionData>
            )}
            <TotalAdvancesCardWrapper />
            {consumptionType === 'PeakValues' && <CapacityRateInformationBox />}
            {consumptionType !== 'Total' && consumptionType !== 'PeakValues' && !isDesktop && (
              <MeterReadingsTable
                consumptionType={(consumptionType as ConsumptionTypeDto) || 'Total'}
                fields={fields.meterReadingsTable}
                serviceDeliveryData={serviceDeliveryPointsData}
              />
            )}
          </Stack>
        </Grid.Item>
      </Grid>
    </Stretch>
  );
};

export default ConsumptionGraphicalOverview;
